import { BarChart } from "@msuite/ui/charts";
import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { array } from "@msuite/utils/array";
import { moment } from "@msuite/utils/date";
import { capFloat, formatNumber } from "@msuite/utils/numbers";
import { useMemo } from "react";
import { useParams } from "react-router";
import { client } from "@/hono";

export function ContactStatistics() {
	/** Hooks */
	const { t } = useTranslation();
	const params = useParams();

	/** Query */
	const { data: statistics } = useQuery({
		queryKey: ["contact", params.contactId ?? "", "statistics"],
		queryFn: async () => {
			if (!params.contactId) throw new Error("Contact ID is required");
			const response = await client.contacts[":contactId"].statistics.$get({
				param: {
					contactId: params.contactId,
				},
			});
			const data = await responseHandler(response);
			const statistics = data.statistics;
			if (!statistics) throw new Error("Statistics not found");
			return statistics;
		},
	});

	/** Memoized */
	const invoicesChartData = useMemo(() => {
		const yearMonths = array(12)
			.map((_, i) => ({
				name: moment().subtract(i, "months").format("MM/YY"),
				month: moment().subtract(i, "months").format("YYYY-MM"),
				totalInvoiceSum: 0,
			}))
			.toReversed();
		for (const invoice of statistics?.invoices ?? []) {
			const invoiceMonth = moment(invoice.creationDate).format("YYYY-MM");
			const index = yearMonths.findIndex((item) => item.month === invoiceMonth);
			const currentYearMonth = yearMonths[index];
			if (currentYearMonth)
				currentYearMonth.totalInvoiceSum = capFloat(currentYearMonth.totalInvoiceSum + invoice.sum);
		}
		return yearMonths;
	}, [statistics?.invoices]);

	return (
		<div className="scrollbar-hidden bottom-space flex flex-col flex-1 gap-4 min-h-0 overflow-y-auto">
			<ContentCard
				title={t("general.overview")}
				icon={icon("ChartPie")}
			>
				<DataGrid>
					<DataGrid.Row>
						<DataGrid.Label>{t("projects.projects")}</DataGrid.Label>
						<DataGrid.Value>{statistics?.totalNumberOfProjects}</DataGrid.Value>
					</DataGrid.Row>
				</DataGrid>
			</ContentCard>
			<ContentCard
				title={t("invoices.invoices")}
				icon={icon("Euro")}
			>
				<DataGrid>
					<DataGrid.Row>
						<DataGrid.Label>{t("invoices.invoices")}</DataGrid.Label>
						<DataGrid.Value>{statistics?.totalNumberOfInvoices}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("controlling.total_invoice_sum")}</DataGrid.Label>
						<DataGrid.Value>{formatNumber(statistics?.totalInvoiceSum, "currency")}</DataGrid.Value>
					</DataGrid.Row>
					<DataGrid.Row>
						<DataGrid.Label>{t("controlling.average_invoice_sum")}</DataGrid.Label>
						<DataGrid.Value>
							{formatNumber(statistics?.averageInvoiceSum, "currency")}
						</DataGrid.Value>
					</DataGrid.Row>
				</DataGrid>
			</ContentCard>
			<ContentCard
				title={t("statistics.statistics")}
				icon={icon("Euro")}
				className="h-100"
				contentClassName="h-full !p-0"
			>
				<BarChart
					data={invoicesChartData}
					dataKeys={["totalInvoiceSum"]}
					formatter={(i) => formatNumber(i, "currency")}
					displayKeyMap={{
						month: t("dates.month"),
						totalInvoiceSum: t("controlling.total_invoice_sum"),
					}}
				/>
			</ContentCard>
		</div>
	);
}
